import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, UserRound } from 'lucide-react';
import { format, addMonths, startOfDay, endOfDay } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { createAppointment } from '@/lib/firebase/queries/scheduling';
import {
  bookableDays,
  coachSlots,
  isLiveBooking,
  pickLeastBookedCoach,
  type AvailabilityWindow,
  type BookingRecord,
} from '@/lib/scheduling/bookingSlots';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface BookSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Coach {
  id: string;
  firstNameAr: string;
  lastNameAr: string;
  firstNameEn: string;
  lastNameEn: string;
  roles: { role: string; isActive: boolean }[];
  role: string;
}

/**
 * Booking (namat#44): by default the beneficiary picks a day, then a time,
 * and the free doctor with the fewest bookings that day is assigned and shown
 * before confirming. «اختيار طبيب» switches to the doctor-first path.
 */
type Mode = 'byDay' | 'byDoctor';

export function BookSessionDialog({ open, onOpenChange }: BookSessionDialogProps) {
  const { t, language, dir } = useLanguage();
  const { user, userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [mode, setMode] = useState<Mode>('byDay');
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string>('');

  const dateLocale = language === 'ar' ? ar : enUS;
  // t is a fresh function every render; the loader reads the latest one
  // without re-running whenever the context renders.
  const tRef = useRef(t);
  tRef.current = t;

  const coachName = (coachId: string) => {
    const coach = coaches.find(c => c.id === coachId);
    if (!coach) return '';
    return language === 'ar'
      ? `${coach.firstNameAr} ${coach.lastNameAr}`
      : `${coach.firstNameEn} ${coach.lastNameEn}`;
  };

  // Doctors, their approved availabilities and every live-or-not booking from
  // today on: enough to pool free times across doctors and count each
  // doctor's bookings on a day.
  const loadBookingData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersSnapshot, availabilitiesSnapshot, appointmentsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'))),
        getDocs(query(collection(db, 'availabilities'), where('status', '==', 'approved'))),
        getDocs(query(
          collection(db, 'appointments'),
          where('date', '>=', Timestamp.fromDate(startOfDay(new Date())))
        )),
      ]);

      const coachesData = usersSnapshot.docs
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            firstNameAr: data.firstNameAr || '',
            lastNameAr: data.lastNameAr || '',
            firstNameEn: data.firstNameEn || '',
            lastNameEn: data.lastNameEn || '',
            roles: data.roles || [],
            role: data.role // Include the old role field for backward compatibility
          };
        })
        .filter(u =>
          // Check both new roles array and old role field
          (u.roles.some((role: { role: string; isActive: boolean }) => role.role === 'coach' && role.isActive)) ||
          u.role === 'coach'
        );

      const windowsData: AvailabilityWindow[] = [];
      availabilitiesSnapshot.docs.forEach(d => {
        const data = d.data();
        if (!data.coachId || !data.startDate || !data.endDate || !data.selectedDays) return;
        windowsData.push({
          coachId: data.coachId,
          startDate: data.startDate.toDate(),
          endDate: data.endDate.toDate(),
          selectedDays: data.selectedDays,
        });
      });

      const bookingsData: BookingRecord[] = [];
      appointmentsSnapshot.docs.forEach(d => {
        const data = d.data();
        if (!data.coachId || !data.date?.toDate) return;
        bookingsData.push({ coachId: data.coachId, date: data.date.toDate(), status: data.status });
      });

      setCoaches(coachesData);
      setWindows(windowsData);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error loading booking data:', error);
      toast.error(tRef.current('coaching.errorFetchingSlots'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setMode('byDay');
      setSelectedDay('');
      setSelectedCoachId('');
      setSelectedSlot('');
      loadBookingData();
    }
  }, [open, loadBookingData]);

  const days = useMemo(
    () => bookableDays(windows, bookings, coaches.map(c => c.id), new Date()),
    [windows, bookings, coaches]
  );
  const dayTimes = useMemo(
    () => days.find(d => d.key === selectedDay)?.slots ?? [],
    [days, selectedDay]
  );
  const doctorSlots = useMemo(
    () => (selectedCoachId ? coachSlots(windows, bookings, selectedCoachId, new Date()) : []),
    [windows, bookings, selectedCoachId]
  );

  // The doctor shown before confirming, and the one that gets booked.
  const assignedCoachId = useMemo(() => {
    if (mode === 'byDoctor') return selectedCoachId;
    const slot = dayTimes.find(s => s.time.getTime().toString() === selectedSlot);
    return slot ? pickLeastBookedCoach(slot.coachIds, bookings, slot.time) ?? '' : '';
  }, [mode, selectedCoachId, dayTimes, selectedSlot, bookings]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setSelectedDay('');
    setSelectedCoachId('');
    setSelectedSlot('');
  };

  const handleSchedule = async () => {
    if (!user) {
      toast.error(t('coaching.mustBeLoggedIn'));
      return;
    }

    const coachId = assignedCoachId;
    if (!coachId || !selectedSlot) {
      toast.error(t('coaching.selectAllFields'));
      return;
    }

    try {
      setLoading(true);
      const slotDate = new Date(parseInt(selectedSlot));
      if (isNaN(slotDate.getTime())) throw new Error('Invalid slot selected');

      // Check if customer already has an appointment with this coach on this day
      const dayStart = startOfDay(slotDate);
      const dayEnd = endOfDay(slotDate);

      const customerCoachDaySnapshot = await getDocs(query(
        collection(db, 'appointments'),
        where('customerId', '==', user.uid),
        where('coachId', '==', coachId),
        where('date', '>=', dayStart),
        where('date', '<=', dayEnd)
      ));

      // A cancelled row stays in place (mobile soft-cancels), so the question
      // is "is any row still live?", not "does a row exist?".
      if (customerCoachDaySnapshot.docs.some(d => isLiveBooking(d.data()))) {
        toast.error(t('coaching.alreadyHasAppointmentWithCoach'));
        await loadBookingData();
        return;
      }

      // The doctor must still be free at exactly this time. This used to
      // refuse when the doctor had ANY appointment that day, which would
      // block every doctor the fewest-bookings rule picks with one booking.
      const coachDaySnapshot = await getDocs(query(
        collection(db, 'appointments'),
        where('coachId', '==', coachId),
        where('date', '>=', dayStart),
        where('date', '<=', dayEnd)
      ));

      const slotTaken = coachDaySnapshot.docs.some(d => {
        const data = d.data();
        return isLiveBooking(data) && data.date?.toDate?.().getTime() === slotDate.getTime();
      });
      if (slotTaken) {
        toast.error(t('coaching.slotTaken'));
        setSelectedSlot('');
        await loadBookingData();
        return;
      }

      // Check if customer already has an appointment
      const customerAppointmentsSnapshot = await getDocs(query(
        collection(db, 'appointments'),
        where('customerId', '==', user.uid),
        where('date', '>=', Timestamp.fromDate(new Date()))
      ));

      if (customerAppointmentsSnapshot.docs.some(d => isLiveBooking(d.data()))) {
        toast.error(t('coaching.alreadyHasAppointment'));
        return;
      }

      // Create the appointment, carrying the customer's names and phone the
      // way mobile does, so the coach sees who booked (namat#36 P1-9).
      await createAppointment(user.uid, coachId, slotDate, userData);

      // Update customer's next appointment date
      const customerRef = doc(db, 'users', user.uid);
      await updateDoc(customerRef, {
        nextAppointment: Timestamp.fromDate(addMonths(slotDate, 1))
      });

      toast.success(t('coaching.sessionBooked'));
      onOpenChange(false);
    } catch (error) {
      console.error('Error booking session:', error);
      if (error instanceof Error && error.message.includes('scheduled availability times')) {
        toast.error(
          language === 'ar'
            ? 'يمكن حجز المواعيد فقط خلال أوقات التوفر المجدولة'
            : 'Appointments can only be booked during scheduled availability times'
        );
      } else {
        toast.error(t('coaching.errorBookingSession'));
      }
    } finally {
      setLoading(false);
    }
  };

  const chip = (selected: boolean) =>
    cn(
      'rounded-md border px-3 py-2 text-sm transition-colors',
      selected
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-input bg-background hover:bg-accent'
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir={dir}>
        <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('coaching.bookSession')}</DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {mode === 'byDay' ? t('coaching.scheduling.byDayDescription') : t('coaching.scheduling.selectCoach')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-start">
            <Button
              type="button"
              variant="link"
              className="h-auto p-0"
              onClick={() => switchMode(mode === 'byDay' ? 'byDoctor' : 'byDay')}
              disabled={loading}
            >
              {mode === 'byDay' ? t('coaching.scheduling.chooseDoctor') : t('coaching.scheduling.anyDoctor')}
            </Button>
          </div>

          {mode === 'byDay' && (
            <>
              <div className="space-y-2">
                <Label>{t('coaching.scheduling.selectDate')}</Label>
                {loading && days.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('coaching.scheduling.loading')}</p>
                ) : days.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('coaching.noAvailableSlots')}</p>
                ) : (
                  <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                    {days.map(day => (
                      <button
                        key={day.key}
                        type="button"
                        className={chip(selectedDay === day.key)}
                        onClick={() => {
                          setSelectedDay(day.key);
                          setSelectedSlot('');
                        }}
                        disabled={loading}
                      >
                        {format(day.date, 'EEEE d MMMM', { locale: dateLocale })}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedDay && (
                <div className="space-y-2">
                  <Label>{t('coaching.scheduling.selectTime')}</Label>
                  <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                    {dayTimes.map(slot => {
                      const value = slot.time.getTime().toString();
                      return (
                        <button
                          key={value}
                          type="button"
                          className={chip(selectedSlot === value)}
                          onClick={() => setSelectedSlot(value)}
                          disabled={loading}
                        >
                          {format(slot.time, 'p', { locale: dateLocale })}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {assignedCoachId && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3 text-sm">
                  <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('coaching.scheduling.assignedDoctor')}:</span>
                  <span className="font-medium">{coachName(assignedCoachId)}</span>
                </div>
              )}
            </>
          )}

          {mode === 'byDoctor' && (
            <>
              <div className="space-y-2">
                <Label>{t('coaching.scheduling.selectCoach')}</Label>
                <Select
                  value={selectedCoachId}
                  onValueChange={(value) => {
                    setSelectedCoachId(value);
                    setSelectedSlot('');
                  }}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('coaching.scheduling.selectCoach')} />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.map((coach) => (
                      <SelectItem key={coach.id} value={coach.id}>
                        {coachName(coach.id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCoachId && (
                <div className="space-y-2">
                  <Label>{t('coaching.scheduling.selectTime')}</Label>
                  <Select
                    value={selectedSlot}
                    onValueChange={setSelectedSlot}
                    disabled={loading || doctorSlots.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        loading ? t('coaching.scheduling.loading') :
                        doctorSlots.length === 0 ? t('coaching.noAvailableSlots') :
                        t('coaching.scheduling.selectTime')
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {doctorSlots.map((slot) => (
                        <SelectItem
                          key={slot.getTime()}
                          value={slot.getTime().toString()}
                        >
                          {format(slot, 'PPP p')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={loading || !assignedCoachId || !selectedSlot}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('coaching.scheduling.scheduling')}
                </>
              ) : (
                t('coaching.bookSession')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
